import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const FONT_FAMILY = "'Sofia Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const TICK_COLOR = "#8a94a3";
const font = (size, weight) => ({ family: FONT_FAMILY, size, weight });

export const PALETTE = {
  brand: "#1789c6",
  success: "#12a970",
  danger: "#e05561",
  warning: "#e89a2c",
  violet: "#7c6ad6",
  slate: "#94a3b8",
};

export const SERIES_COLORS = [
  PALETTE.brand,
  PALETTE.success,
  PALETTE.warning,
  PALETTE.violet,
  PALETTE.danger,
  PALETTE.slate,
];

export const formatNumber = (n) => (Number(n) || 0).toLocaleString("en-US");

export const formatMoney = (n, { compact = false } = {}) => {
  const num = Number(n) || 0;
  if (compact && Math.abs(num) >= 1000) {
    return `$${num.toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 1 })}`;
  }
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Scriptable vertical gradient for area fills; falls back to a flat tint before layout. */
export const areaGradient = (hex) => (context) => {
  const { chart } = context;
  const { ctx, chartArea } = chart;
  if (!chartArea) return hexToRgba(hex, 0.12);
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, hexToRgba(hex, 0.22));
  gradient.addColorStop(1, hexToRgba(hex, 0));
  return gradient;
};

/** Dataset styling shared by bar and line views of the same series. */
export const seriesDataset = ({ label, data, color, type, fill = false }) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: type === "line" ? (fill ? areaGradient(color) : color) : color,
  hoverBackgroundColor: type === "line" ? undefined : hexToRgba(color, 0.85),
  borderWidth: type === "line" ? 2 : 0,
  borderRadius: type === "line" ? 0 : 4,
  borderSkipped: false,
  maxBarThickness: 18,
  categoryPercentage: 0.7,
  barPercentage: 0.9,
  tension: 0.35,
  fill: type === "line" && fill,
  pointRadius: 0,
  pointHoverRadius: 4,
  pointHoverBorderWidth: 2,
  pointHoverBorderColor: "#fff",
  pointBackgroundColor: color,
});

const tooltip = (valueFormatter) => ({
  backgroundColor: "#0f172a",
  titleColor: "#fff",
  bodyColor: "#e2e8f0",
  titleFont: font(12, "600"),
  bodyFont: font(12),
  padding: 10,
  cornerRadius: 8,
  boxWidth: 8,
  boxHeight: 8,
  boxPadding: 4,
  usePointStyle: true,
  callbacks: valueFormatter
    ? { label: (ctx) => ` ${ctx.dataset.label}: ${valueFormatter(ctx.parsed.y)}` }
    : undefined,
});

/** Cartesian (bar / line) options. Pass `money` to format the value axis as currency. */
export const cartesianOptions = ({ money = false, stacked = false } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  layout: { padding: { top: 4 } },
  scales: {
    y: {
      beginAtZero: true,
      stacked,
      grid: { color: "#eef1f5", drawTicks: false },
      border: { display: false },
      ticks: {
        padding: 10,
        precision: money ? undefined : 0,
        maxTicksLimit: 6,
        color: TICK_COLOR,
        font: font(11),
        callback: money ? (v) => formatMoney(v, { compact: true }) : undefined,
      },
    },
    x: {
      stacked,
      grid: { display: false },
      border: { display: false },
      ticks: { padding: 6, autoSkipPadding: 12, maxRotation: 0, color: TICK_COLOR, font: font(11) },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: tooltip(money ? (v) => formatMoney(v) : (v) => formatNumber(v)),
  },
});

export const doughnutOptions = (valueFormatter = formatNumber) => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: "74%",
  plugins: {
    legend: { display: false },
    tooltip: {
      ...tooltip(),
      callbacks: { label: (ctx) => ` ${ctx.label}: ${valueFormatter(ctx.parsed)}` },
    },
  },
});

/** True when every numeric value in the given arrays is zero / missing. */
export const isAllZero = (...arrays) =>
  arrays.every((arr) => !arr || arr.every((v) => !Number(v)));
